import { ICellRendererParams } from "ag-grid-community";
import CampaignTagsDropDown from "../drop-downs/tags/CampaignTagsDropDown";
import { useEffect, useMemo, useState, memo } from "react";
import { useSelector } from "react-redux";
import { getActiveModule, createWorkers } from "@ts/utils";
import { SKU_DEPENDENT_MODULES, TAGS_MODULE_MAPPING } from "@ts/constants";
import {
    SubModule,
    TClientTags,
    TagModules,
    TagWorkerResult,
    TagWorkerType,
    TagsMap,
} from "@ts/common-types";
import { includes, map } from "underscore";
import { processTagData } from "@/workers/tag.worker";

// Memoize the component to prevent unnecessary re-renders
const TagsCellRenderer = memo(
    (params: ICellRendererParams) => {
        const { activeModule } = getActiveModule();
        const deepDiveGridType: TagModules | null = useMemo(
            () =>
                includes(SKU_DEPENDENT_MODULES, activeModule)
                    ? SubModule.CAMPAIGN
                    : null,
            [activeModule]
        );

        // Get tag type list from Redux store
        const tagTypeList = useSelector(
            (state: { tagTypeList: { value: TClientTags[] } }) =>
                state.tagTypeList.value
        );

        const { recordType, validRecordTypes } =
            TAGS_MODULE_MAPPING[
            deepDiveGridType ?? (activeModule as keyof TagsMap)
            ];

        // State for available tags
        const [avlTagsData, setAvlTagsData] = useState<string[]>([]);
        const [isProcessing, setIsProcessing] = useState(false);

        // Process tag data using worker or fallback
        useEffect(() => {
            let isMounted = true;
            setIsProcessing(true);

            const processTags = async () => {
                if (window.Worker) {
                    try {
                        const result: any = await createWorkers("tag", {
                            type: TagWorkerType.PROCESS_TAGS,
                            payload: {
                                tagTypeList,
                                recordType,
                                validRecordTypes,
                            },
                        });
                        if (
                            isMounted &&
                            result.type === TagWorkerResult.PROCESS_TAGS_RESULT
                        ) {
                            setAvlTagsData(result.payload);
                            setIsProcessing(false);
                        }
                    } catch (e) {
                        setIsProcessing(false);
                    }
                } else {
                    // Fallback logic
                    const mainTagsData = processTagData({
                        tagTypeList,
                        recordType,
                        validRecordTypes,
                    }) as string[];
                    if (isMounted) {
                        setAvlTagsData(mainTagsData);
                        setIsProcessing(false);
                    }
                }
            };
            processTags();
            return () => {
                isMounted = false;
            };
        }, [tagTypeList, recordType, validRecordTypes]);

        // Get current tags from params data
        const currentTags = useMemo(
            () =>
                map(
                    params.colDef.field === "tags_map"
                        ? JSON.parse(params.data[params.colDef.field])
                        : params.data[params.colDef.field] || [],
                    (tag) => `${tag.tag_id}##${tag.tag_name}`
                ),
            [params.data, params.colDef.field]
        );

        // Memoize available tags not in current tags
        const avlDataNotInCurrentTags = useMemo(() => {
            if (!avlTagsData?.length || !currentTags?.length)
                return avlTagsData;
            return avlTagsData.filter((avl) => !currentTags.includes(avl));
        }, [currentTags, avlTagsData]);

        // State for available tags constant
        const [avlTagsConstant, setAvlTagsConstant] = useState<string[]>([]);

        // Update available tags constant when dependencies change
        useEffect(() => {
            setAvlTagsConstant(avlDataNotInCurrentTags || avlTagsData);
        }, [avlDataNotInCurrentTags, avlTagsData]);

        // Memoize the record ID and name
        const recordId = useMemo(
            () =>
                params.data[
                TAGS_MODULE_MAPPING[
                    deepDiveGridType || (activeModule as keyof TagsMap)
                ].recordId
                ],
            [params.data, deepDiveGridType, activeModule]
        );

        const recordName = useMemo(
            () =>
                params.data[
                TAGS_MODULE_MAPPING[
                    deepDiveGridType || (activeModule as keyof TagsMap)
                ].recordName
                ],
            [params.data, deepDiveGridType, activeModule]
        );

        // Early return for pinned rows
        if (params.node.rowPinned) {
            return null;
        }

        return (
            <CampaignTagsDropDown
                rowId={params.node.id}
                activeModule={deepDiveGridType || activeModule}
                {...{
                    params,
                    recordId,
                    recordName,
                    avlTagsConstant,
                    setAvlTagsConstant,
                    currentTags,
                    activeModule,
                    isProcessing,
                    avlDataNotInCurrentTags,
                }}
            />
        );
    },
    (prevProps, nextProps) => {
        // Custom comparison function for memo
        return (
            prevProps.data[prevProps.colDef.field] ===
            nextProps.data[nextProps.colDef.field] &&
            prevProps.node.id === nextProps.node.id &&
            prevProps.node.rowPinned === nextProps.node.rowPinned
        );
    }
);

export default TagsCellRenderer;
